/* StateExplorer — OwnPath v20 state-first entrepreneurship explorer (28 states, JSON-driven).
 * Order: State → Government schemes → Region/city filter → State opportunities → Common ideas →
 * Learning → Seven-day test → Budget calculator → Formalisation.  Read-only; never touches keep/lock flow. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, MapPin, Landmark, Store, Utensils, GraduationCap, FlaskConical, Calculator, FileCheck2, Search, X, Filter } from 'lucide-react';
import { SectionHead, StatusLegend, Badge } from './ownpathUi';
import GovSchemeCard from './GovSchemeCard';
import OpportunityCard from './OpportunityCard';
import CommonIdeaCard from './CommonIdeaCard';
import LearningResources from './LearningResources';
import SevenDayJourney from './SevenDayJourney';
import BudgetCalculator from './BudgetCalculator';
import Formalisation from './Formalisation';

const API = process.env.REACT_APP_BACKEND_URL;
const chip = (on) => `px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${on ? 'bg-rose-600 text-white border-rose-600' : 'bg-white text-gray-800 border-gray-300 hover:border-rose-400'}`;

export default function StateExplorer({ initialState = '' }) {
  const [states, setStates] = useState([]);
  const [meta, setMeta] = useState({});
  const [stateId, setStateId] = useState(initialState);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [region, setRegion] = useState('');
  const [city, setCity] = useState('');
  const [marketType, setMarketType] = useState('');
  const [q, setQ] = useState('');
  const [natOpen, setNatOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const testRef = useRef(null);
  const budgetRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/ownpath/v20/states`).then((r) => { setStates(r.data.states || []); setMeta(r.data); }).catch(() => setErr('Could not load states.'));
  }, []);

  useEffect(() => {
    if (!stateId) { setData(null); return; }
    setLoading(true); setErr(''); setRegion(''); setCity(''); setMarketType(''); setSelected(null); setQ('');
    axios.get(`${API}/api/ownpath/v20/state/${stateId}`).then((r) => setData(r.data)).catch(() => setErr('Could not load this state.')).finally(() => setLoading(false));
    const u = new URL(window.location.href); u.searchParams.set('state', stateId); window.history.replaceState({}, '', u);
  }, [stateId]);

  const cityObj = useMemo(() => (data?.marketExamples || []).find((m) => m.name === city), [data, city]);
  const effMarket = cityObj?.type || marketType;
  const needle = q.trim().toLowerCase();
  const match = (blob) => !needle || blob.toLowerCase().includes(needle);

  const schemes = useMemo(() => (data?.schemes || []).filter((s) => match(`${s.title} ${s.purpose} ${s.type} ${s.supportTypes.join(' ')} ${s.whoItIsFor.join(' ')}`)), [data, needle]); // eslint-disable-line react-hooks/exhaustive-deps
  const national = useMemo(() => (data?.nationalCommon || []).filter((s) => match(`${s.title} ${s.purpose} ${s.type}`)), [data, needle]); // eslint-disable-line react-hooks/exhaustive-deps

  const { oppMatched, oppOther } = useMemo(() => {
    const all = (data?.opportunityCards || []).filter((c) => match(`${c.title} ${c.category} ${c.localOpportunityLogic} ${c.regionAndDistrictClusters.join(' ')}`));
    if (!region) return { oppMatched: all, oppOther: [] };
    return { oppMatched: all.filter((c) => c.regionMatches.includes(region)), oppOther: all.filter((c) => !c.regionMatches.includes(region)) };
  }, [data, region, needle]); // eslint-disable-line react-hooks/exhaustive-deps

  const ideas = useMemo(() => {
    const all = (data?.commonIdeas || []).filter((i) => match(`${i.title} ${i.category} ${i.demandLogic}`));
    const rec = new Set([...(cityObj?.recommendedIdeaIds || []), ...(effMarket ? (data?.marketTypes?.[effMarket]?.ideaIds || []) : [])]);
    const score = (i) => (rec.has(i.id) ? 0 : 1) * 10 + (i.featured ? 0 : 1);
    return { list: [...all].sort((a, b) => score(a) - score(b)), rec };
  }, [data, cityObj, effMarket, needle]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (rec, ref) => { setSelected(rec); setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60); };

  return (
    <section className="rounded-3xl border border-rose-100 bg-white p-5 md:p-7 shadow-sm space-y-8" data-testid="v20-explorer">
      <div>
        <SectionHead n={1} icon={MapPin} title="Select your state" sub={`${meta.count || 28} states · verified ${meta.verifiedAt || '2026-09-01'} · official sources only`} testid="v20-h-state" />
        <select value={stateId} onChange={(e) => setStateId(e.target.value)} className="w-full px-3 py-3 rounded-xl border-2 border-rose-200 bg-white text-sm font-semibold text-gray-900 focus:outline-none focus:border-rose-500" data-testid="v20-state-select">
          <option value="">Choose your state…</option>
          {states.map((s) => <option key={s.id} value={s.id}>{s.name} — {s.schemeCount} schemes · {s.opportunityCount} local opportunities</option>)}
        </select>
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid="v20-state-chips">
          {states.map((s) => <button key={s.id} type="button" onClick={() => setStateId(s.id)} className={chip(stateId === s.id)} data-testid={`v20-state-${s.id}`}>{s.name}</button>)}
        </div>
        {meta.disclaimer && <p className="text-[11px] text-gray-500 mt-3">{meta.disclaimer}</p>}
      </div>

      {err && <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="v20-error">{err}</p>}
      {loading && <p className="text-sm text-gray-500 text-center py-6" data-testid="v20-loading"><Loader2 className="inline w-4 h-4 animate-spin mr-2" />Loading…</p>}

      {data && !loading && (
        <>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${data.name} schemes, opportunities and ideas…`} className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-rose-400" data-testid="v20-search" />
            {q && <button type="button" onClick={() => setQ('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400" data-testid="v20-search-clear"><X size={14} /></button>}
          </div>

          <div data-testid="v20-gov-section">
            <SectionHead n={2} icon={Landmark} title={`Government schemes and official support — ${data.name}`} sub={data.verificationRule || 'Existence and official source verified. Current intake, eligibility, benefit and deadlines must be rechecked on the official source.'} testid="v20-h-gov" />
            <div className="mb-3"><StatusLegend /></div>
            <div className="space-y-3" data-testid="v20-state-schemes">
              {schemes.map((s) => <GovSchemeCard key={s.id} s={s} />)}
              {schemes.length === 0 && <p className="text-sm text-gray-500 text-center py-4">No state schemes match your search.</p>}
            </div>
            <div className="mt-5 rounded-2xl border border-indigo-100 bg-indigo-50/40 p-4" data-testid="v20-national-section">
              <button type="button" onClick={() => setNatOpen((v) => !v)} className="w-full flex items-center justify-between gap-2 text-left" data-testid="v20-national-toggle" aria-expanded={natOpen}>
                <div><p className="text-[10px] uppercase tracking-widest font-bold text-indigo-800">National common schemes that fit {data.name}</p><p className="text-xs text-gray-600 mt-0.5">{national.length} central routes · shown separately from state programmes</p></div>
                <span className="text-xs font-bold text-indigo-800">{natOpen ? 'Hide' : 'Show'}</span>
              </button>
              {natOpen && <div className="space-y-3 mt-4" data-testid="v20-national-schemes">{national.map((s) => <GovSchemeCard key={s.id} s={s} testidPrefix="v20-nat" />)}</div>}
            </div>
          </div>

          <div data-testid="v20-region-section">
            <SectionHead n={3} icon={Filter} title="Select region, district, nearest city or market type" sub={data.districtSelectorRule} testid="v20-h-region" />
            <div className="space-y-3">
              <div className="flex flex-wrap gap-1.5" data-testid="v20-region-chips">
                <button type="button" onClick={() => setRegion('')} className={chip(!region)} data-testid="v20-region-all">All regions</button>
                {data.regionProfiles.map((r) => <button key={r} type="button" onClick={() => setRegion(r)} className={chip(region === r)} data-testid={`v20-region-${r.replace(/\W+/g, '-').toLowerCase()}`}>{r}</button>)}
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <select value={city} onChange={(e) => { setCity(e.target.value); setMarketType(''); }} className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm" data-testid="v20-city-select">
                  <option value="">Nearest city (examples)…</option>
                  {data.marketExamples.map((m) => <option key={m.name} value={m.name}>{m.name} — {m.label}</option>)}
                </select>
                <select value={effMarket} onChange={(e) => { setMarketType(e.target.value); setCity(''); }} className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm" data-testid="v20-market-select">
                  <option value="">Market type…</option>
                  {Object.entries(data.marketTypes || {}).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              {data.navigationCaveat && <p className="text-[11px] text-gray-500">{data.navigationCaveat}</p>}
            </div>
          </div>

          <div data-testid="v20-opp-section">
            <SectionHead n={4} icon={Store} title={`State-specific business opportunities — ${data.name}`} sub="Official district, ODOP, GI, agricultural, tourism or craft evidence confirms local relevance only. It does not prove demand, profit or guaranteed income." testid="v20-h-opp" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="v20-opp-grid">
              {oppMatched.map((c) => <OpportunityCard key={c.id} c={c} onTest={(r) => pick(r, testRef)} onBudget={(r) => pick(r, budgetRef)} />)}
            </div>
            {oppMatched.length === 0 && <p className="text-sm text-gray-500 text-center py-4">No cards are mapped to this region/search.</p>}
            {oppOther.length > 0 && (
              <div className="mt-5" data-testid="v20-opp-other">
                <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">Other opportunities elsewhere in {data.name}</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 opacity-90">{oppOther.map((c) => <OpportunityCard key={c.id} c={c} onTest={(r) => pick(r, testRef)} onBudget={(r) => pick(r, budgetRef)} />)}</div>
              </div>
            )}
          </div>

          <div data-testid="v20-ideas-section">
            <SectionHead n={5} icon={Utensils} title="Common youth-friendly business ideas" sub={effMarket ? `Prioritised for ${city || data.marketTypes?.[effMarket]?.label}. Locally common does not mean profitable — test first.` : 'Familiar, accessible ideas. Pick a city or market type above to prioritise. Locally common does not mean profitable.'} testid="v20-h-ideas" />
            {effMarket && data.marketTypes?.[effMarket]?.demandSignals && <div className="flex flex-wrap gap-1.5 mb-3">{data.marketTypes[effMarket].demandSignals.map((d, i) => <Badge key={i} tone="blue">{d}</Badge>)}</div>}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="v20-ideas-grid">
              {ideas.list.map((i) => <CommonIdeaCard key={i.id} i={i} recommended={ideas.rec.has(i.id)} onTest={(r) => pick(r, testRef)} onBudget={(r) => pick(r, budgetRef)} />)}
            </div>
          </div>

          <div>
            <SectionHead n={6} icon={GraduationCap} title="Verified learning resources" sub="Official government portals, institutions and official channels only. Views, likes, Reels and testimonials are never proof of earnings." testid="v20-h-learn" />
            <LearningResources resources={data.learningResources} media={data.officialLearningMedia} policy={data.socialPolicy} />
          </div>

          <div ref={testRef}>
            <SectionHead n={7} icon={FlaskConical} title="Seven-day paid market test" sub="Every idea passes through the same evidence-first validation before any large spend." testid="v20-h-test" />
            <SevenDayJourney selected={selected} />
          </div>

          <div ref={budgetRef}>
            <SectionHead n={8} icon={Calculator} title="Budget, costs and owner-surplus calculator" sub={data.incomePolicy?.websiteDisplay} testid="v20-h-budget" />
            <BudgetCalculator selected={selected} models={data.budgetModels} policy={data.incomePolicy} />
          </div>

          <div>
            <SectionHead n={9} icon={FileCheck2} title="Formalisation and official application routes" sub="Apply only after demand, safety and eligibility evidence. Every CTA opens the official source — no direct Apply buttons are enabled." testid="v20-h-formal" />
            <Formalisation data={data} />
          </div>
        </>
      )}
    </section>
  );
}
